import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatMessage } from '../types/energyData';
import { EnergyDataService } from '../services/energyDataService';
import { apiService } from '../services/apiService';
import { ChartData } from '../services/chartDataGenerator';
import ChatMessageComponent from './ChatMessage';
import QuickQueries from './QuickQueries';
import { Send, AlertCircle, ChevronUp } from 'lucide-react';

interface ChatInterfaceProps {
  energyDataService: EnergyDataService | null; // Made optional - not actually used by chat
  siteId: string;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ energyDataService, siteId }) => {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [viewportHeight, setViewportHeight] = useState<number>(window.innerHeight);
  const [isQuickQueriesVisible, setIsQuickQueriesVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 640;
      setIsMobile(mobile);
      // Don't auto-show on desktop - let user control it
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Add welcome message - update when language changes
    // Add a small delay for better UX (shows loading spinner briefly)
    const timer = setTimeout(() => {
      const welcomeMessage: ChatMessage = {
        id: 'welcome',
        type: 'bot',
        content: t('chat.welcomeMessage'),
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
      setIsInitialLoading(false);
    }, 300); // Small delay to show spinner

    return () => clearTimeout(timer);
  }, [t, i18n.language]); // React to language changes

  // Handle viewport height changes (mobile keyboard, browser UI)
  useEffect(() => {
    const updateViewportHeight = () => {
      // Use visual viewport API if available (better for mobile)
      if (window.visualViewport) {
        setViewportHeight(window.visualViewport.height);
      } else {
        setViewportHeight(window.innerHeight);
      }
    };

    // Initial height
    updateViewportHeight();

    // Listen to viewport changes
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewportHeight);
      window.visualViewport.addEventListener('scroll', updateViewportHeight);
    } else {
      window.addEventListener('resize', updateViewportHeight);
    }

    // Prevent body scroll when input is focused (mobile)
    const handleFocus = () => {
      document.body.style.overflow = 'hidden';
    };
    const handleBlur = () => {
      document.body.style.overflow = '';
    };

    const input = inputRef.current;
    if (input) {
      input.addEventListener('focus', handleFocus);
      input.addEventListener('blur', handleBlur);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateViewportHeight);
        window.visualViewport.removeEventListener('scroll', updateViewportHeight);
      } else {
        window.removeEventListener('resize', updateViewportHeight);
      }
      if (input) {
        input.removeEventListener('focus', handleFocus);
        input.removeEventListener('blur', handleBlur);
      }
      document.body.style.overflow = '';
    };
  }, []);

  const handleSendMessage = async (messageText: string) => {
    if (!messageText.trim() || isLoading || !siteId) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setError(null);

    try {
      // Send message to API
      const response = await apiService.sendChatMessage(siteId, messageText, conversationId);
      
      // Update conversation ID if this is a new conversation
      if (!conversationId && response.conversationId) {
        setConversationId(response.conversationId);
      }

      // Backend MUST always generate chartData when chart is requested
      // No frontend fallback - backend is responsible for all chart generation
      const finalChartData: ChartData | null | undefined = response.chartData;
      
      if (response.chartHint && !finalChartData) {
        console.warn('Backend returned ChartHint but no ChartData - this should not happen');
      }

      const botMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'bot',
        content: response.message,
        timestamp: new Date(response.timestamp),
        chartData: finalChartData ?? undefined,
        chartHint: response.chartHint
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      setError(errorMessage);
      
      // Show user-friendly error message in chat
      let userFriendlyMessage = errorMessage;
      if (errorMessage.includes('timed out') || errorMessage.includes('timeout')) {
        userFriendlyMessage = 'Request timed out. This can happen on slower connections. Please try:\n\n• A simpler question\n• Checking your internet connection\n• Trying again in a moment';
      } else if (errorMessage.includes('No data available')) {
        userFriendlyMessage = 'No data available for this site.\n\nThis could mean:\n• The site has no sensor data yet\n• The requested time range has no data\n• Try asking about a different time period or metric';
      }
      
      const errorBotMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'bot',
        content: userFriendlyMessage,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorBotMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickQuery = (query: string) => {
    setInputValue(query);
    handleSendMessage(query);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSendMessage(inputValue);
  };

  return (
    <div 
      className="flex flex-col bg-white mobile-chat-container relative"
      style={{ 
        height: `${viewportHeight}px`,
        maxHeight: '100dvh',
        minHeight: '100svh'
      }}
    >

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border-b border-red-200 flex-shrink-0">
          <div className="flex items-center gap-2 text-red-800 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Initial Loading Spinner */}
      {isInitialLoading && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-soleco-200 border-t-soleco-500 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500 text-sm">Loading chat...</p>
          </div>
        </div>
      )}

      {/* Messages - with padding bottom for fixed input area and Quick Queries */}
      {!isInitialLoading && (
        <div 
          className="flex-1 overflow-y-auto px-2 py-4 space-y-4 overscroll-contain"
          style={{ 
            paddingBottom: isMobile 
              ? (isQuickQueriesVisible ? '280px' : '100px')
              : (isQuickQueriesVisible ? '280px' : '100px') // Same padding on desktop for fixed Quick Queries
          }}
        >
          {messages.map((message) => (
            <ChatMessageComponent key={message.id} message={message} />
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="flex items-center gap-2 px-4 py-3 bg-gray-100 rounded-2xl rounded-bl-md">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center border border-gray-200">
                  <img src="/logo.svg" alt="Soleco Logo" className="w-4 h-4" />
                </div>
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Quick Queries - Fixed at bottom on mobile and desktop */}
      {!isInitialLoading && isQuickQueriesVisible && (
        <div 
          className={`border-t border-gray-200 bg-gray-50 ${
            isMobile ? 'fixed left-0 right-0 z-30 shadow-lg' : 'fixed left-0 right-0 z-30 shadow-lg'
          } safe-area-bottom`}
          style={isMobile ? {
            bottom: '80px',
            paddingBottom: 'calc(0.5rem + var(--safe-area-inset-bottom, 0px))'
          } : {
            bottom: '80px',
            paddingBottom: '0.5rem'
          }}
        >
          {/* Toggle button inside Quick Queries section - shown on both mobile and desktop */}
          <button
            onClick={() => setIsQuickQueriesVisible(false)}
            className="w-full py-2 px-4 border-b border-gray-200 bg-gray-100 text-soleco-600 text-sm font-medium flex items-center justify-center gap-2 touch-manipulation hover:bg-gray-200 transition-colors"
            aria-label="Hide quick queries"
          >
            <ChevronUp className="w-4 h-4" />
            <span>Hide Quick Queries</span>
          </button>
          <div className="p-4">
            <QuickQueries onQuerySelect={handleQuickQuery} />
          </div>
        </div>
      )}

      {/* Toggle button - Show when Quick Queries is hidden (both mobile and desktop) */}
      {!isInitialLoading && !isQuickQueriesVisible && (
        <button
          onClick={() => setIsQuickQueriesVisible(true)}
          className="w-full py-2 px-4 border-t border-gray-200 bg-gray-100 text-soleco-600 text-sm font-medium flex items-center justify-center gap-2 touch-manipulation hover:bg-gray-200 transition-colors fixed left-0 right-0 z-30 safe-area-bottom"
          style={{
            bottom: '80px',
            paddingBottom: isMobile ? 'calc(0.5rem + var(--safe-area-inset-bottom, 0px))' : '0.5rem'
          }}
          aria-label="Show quick queries"
        >
          <ChevronUp className="w-4 h-4 rotate-180" />
          <span>Show Quick Queries</span>
        </button>
      )}

      {/* Input - Fixed at bottom on both mobile and desktop */}
      {!isInitialLoading && (
        <div 
          className="p-4 border-t border-gray-200 bg-white fixed bottom-0 left-0 right-0 z-40 shadow-lg safe-area-bottom"
          style={{
            paddingBottom: isMobile ? 'calc(1rem + var(--safe-area-inset-bottom, 0px))' : '1rem'
          }}
        >
        <form onSubmit={handleSubmit} className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={t('chat.placeholder')}
            inputMode="text"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="sentences"
            className="flex-1 px-4 py-3.5 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-soleco-500 focus:border-transparent touch-manipulation"
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || isLoading}
            className="min-w-[44px] min-h-[44px] px-4 py-3.5 bg-soleco-500 text-white rounded-lg hover:bg-soleco-600 active:bg-soleco-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center gap-2 touch-manipulation"
            aria-label={t('chat.send')}
          >
            <Send className="w-5 h-5" />
            <span className="hidden sm:inline">{t('chat.send')}</span>
          </button>
        </form>
        </div>
      )}
    </div>
  );
};

export default ChatInterface;
